import { ArrowDownIcon, ChevronDownIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react"
import { BigNumber } from "@ethersproject/bignumber"
import { useFormik } from "formik"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { type Address, formatEther, parseUnits } from "viem"
import * as Yup from "yup"
import type { NextRouter } from "next/router"

import EligibleBadge from "@/stake/components/staker/EligibleBadge"
import { useUserDRS } from "@/stake/hooks/useUserDRS"
import SmallDelegateUserIdentity from "@/common/components/columns/SmallDelegateUserIdentity"
import ConnectAccountButton from "@/web3/components/ConnectAccountButton"
import { getBalanceAsync } from "@/token/helpers/erc20"
import { truncateToMaxDecimals } from "@/common/helpers/number"
import DelegateeTypeSelector, {
  DelegationType,
} from "@/governance/components/lst-stake/DelegateeTypeSelector"
import type { Organization } from "@/query/graphql"
import { useAccountsByAddressQuery } from "@/query/graphql"
import { useLSTStakerData } from "@/stake/hooks/useLSTStakerData"
import {
  type StakeFunction,
  type UnstakeFunction,
} from "@/stake/hooks/useLSTStakerTransaction"
import { useLstGovConversion } from "@/stake/hooks/useLstGovConversion"
import { SwapIcon } from "@/token/components/TokenWrapperContent"
import { shortenIfAddress } from "@/web3/helpers/address"
import { useAddress } from "@/web3/hooks/useAddress"
import { ROUTES } from "@/common/constants/routes"
import { getLSTStakerConstants } from "@/governance/constants/lststaker"
import { isFeatureFlagOn } from "@/governance/context"
import { FeatureFlag } from "@/organization/providers/OrganizationProvider"
import type { StakeType } from "@/stake/types"

export const checkStakingBalances =
  (
    router: NextRouter,
    isWhiteLabel: boolean,
    organization?: Organization,
    stakeType?: StakeType,
  ) =>
  async (address: string) => {
    // only check if org has features
    if (!organization?.features) return
    const isLstEnabled = isFeatureFlagOn(
      organization.features,
      FeatureFlag.ShowLSTStaking,
    )

    // only check if LST is enabled
    if (!isLstEnabled) return

    const routes = [
      ROUTES.governance.stake.deposit(organization.slug, stakeType),
      ROUTES.dao.stake.deposit(),
    ]

    // Only check if is /stake/deposit or /dao/stake/deposit pages
    if (routes.some((route) => router.asPath.includes(route))) {
      const { lstToken } = getLSTStakerConstants(organization?.id)
      try {
        const balance = await getBalanceAsync(
          address as Address,
          lstToken.address as Address,
        )

        if (balance && BigNumber.from(balance).gt(BigNumber.from(0))) {
          if (isWhiteLabel) {
            await router.push(ROUTES.dao.stake.index(stakeType))
          } else {
            await router.push(
              ROUTES.governance.stake.lst.index(organization.slug),
            )
          }
        }
      } catch {}
    }
  }

export enum ActionMode {
  STAKE = "stake",
  UNSTAKE = "unstake",
}

type Props = {
  organization: Organization
  onShowDelegatesLayout: () => void
  defaultDelegatee: string | null
  stakeValue: string
  onSetStakeValue: (value: string) => void
  mode: ActionMode
  handleStake: StakeFunction
  handleUnstake: UnstakeFunction
  isWhiteLabel: boolean
}

type TokenWrapperValues = {
  stakeValue: string
  receivedValue: string
  delegatee: Address
}

const StakingFormWrapper: FC<Props> = ({
  onShowDelegatesLayout,
  defaultDelegatee,
  stakeValue,
  onSetStakeValue,
  mode,
  organization,
  handleStake,
  handleUnstake,
  isWhiteLabel,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const address = useAddress()
  const isDefaultDelegateeSelf = defaultDelegatee === address
  const [delegationType, setDelegationType] = useState<DelegationType>(
    isDefaultDelegateeSelf ? DelegationType.SELF : DelegationType.CUSTOM,
  )
  const [displayedDelegatee, setDisplayedDelegatee] = useState(
    isDefaultDelegateeSelf
      ? "My self"
      : defaultDelegatee
      ? shortenIfAddress(defaultDelegatee)
      : "Select delegate",
  )
  const {
    orgToken,
    lstToken,
    user: { balances: userBalances },
    allowNonEligibleDelegates,
  } = useLSTStakerData({ orgId: organization.id })
  const { govTokensToLst, lstTokensToGov } = useLstGovConversion({
    stakerContractId: lstToken.id,
  })

  const isWithdrawMode = mode === ActionMode.UNSTAKE

  const onSubmit = (values: TokenWrapperValues) => {
    if (isWithdrawMode) {
      handleUnstake({
        amount: values.stakeValue,
        receivedValue: values.receivedValue,
      })
    } else {
      handleStake({
        amount: values.stakeValue,
        receivedValue: values.receivedValue,
        delegatee: values.delegatee,
      })
    }
  }

  const maxLST = formatEther(BigInt(userBalances.lstToken.value.toString()))
  const maxOrg = formatEther(BigInt(userBalances.orgToken.value.toString()))

  const maxValue = isWithdrawMode ? maxLST : maxOrg

  const tokensAvailable = useMemo(() => {
    const maxValueNumber = Number(maxValue)

    if (maxValueNumber > 0) return true

    return false
  }, [maxValue])

  const {
    errors,
    touched,
    values,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = useFormik<TokenWrapperValues>({
    onSubmit,
    initialValues: {
      stakeValue: stakeValue,
      receivedValue: "0",
      delegatee: (defaultDelegatee ?? "") as Address,
    },
    validationSchema: Yup.object({
      stakeValue: Yup.string()
        .required("Required field")
        .test(
          "greaterThanZero",
          "Must be greater than 0",
          (value) => value !== undefined && Number(value) > 0,
        )
        .test("maxValue", `Must be less than ${maxValue}`, (value) => {
          const valueNumber = BigNumber.from(
            parseUnits(value ?? "0", orgToken.decimals),
          )
          const maxValueNumber = BigNumber.from(
            parseUnits(maxValue ?? "0", orgToken.decimals),
          )

          return valueNumber.lte(maxValueNumber)
        }),
      delegatee: Yup.string()
        .required("Required field")
        .test("eligible", "Delegate is not eligible", () => {
          if (!isDRSActive || allowNonEligibleDelegates) return true
          if (!isDRSElegible) return false

          return true
        }),
    }),
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: false,
  })

  const {
    isDRSElegible,
    isDRSActive,
    isLoading: isDRSLoading,
  } = useUserDRS({
    address: values.delegatee,
    organization,
  })

  const { data: delegateeAccountData, isLoading: delegateeAccountLoading } =
    useAccountsByAddressQuery(
      {
        addresses: [values.delegatee as Address],
      },
      { enabled: Boolean(values.delegatee) },
    )
  const delegateeAccount = delegateeAccountData?.accounts[0]

  const castInput = (value: string): string => {
    if (!value) return ""

    let validInput = value.replace(/[^0-9.]/g, "")
    const parts = validInput.split(".")

    if (parts[0] !== undefined) {
      parts[0] = parts[0].replace(/^0+/, "")
      if (parts[0] === "") parts[0] = "0"
    }

    if (parts.length > 1) {
      validInput = parts[0] + "." + parts.slice(1).join("")
    } else {
      validInput = parts.join("")
    }

    const decimals = isWithdrawMode ? lstToken.decimals : orgToken.decimals

    return truncateToMaxDecimals(validInput, decimals)
  }

  const handleChangeField = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
  ) => {
    const value = castInput(event.target?.value)
    await setFieldValue(fieldName, value, true)
    await setFieldTouched(fieldName, true, true)
    onSetStakeValue(value)
  }

  const prevMode = useRef(mode)

  const updateReceivedValue = (value: string) => {
    const method = isWithdrawMode ? lstTokensToGov : govTokensToLst
    const _value = method(value)
    setFieldValue("receivedValue", _value, false)
  }

  const handleDelegationChange = (type: DelegationType | null) => {
    if (!type) return
    setDelegationType(type)
    switch (type) {
      case DelegationType.SELF:
        setFieldValue("delegatee", address, true)
        setDisplayedDelegatee(shortenIfAddress(address))
        onClose()
        break
      case DelegationType.CUSTOM:
        onShowDelegatesLayout()
        onClose()
        break
    }

    setFieldTouched("delegatee", true, true)
  }

  useEffect(() => {
    if (defaultDelegatee) {
      setDelegationType(
        isDefaultDelegateeSelf ? DelegationType.SELF : DelegationType.CUSTOM,
      )
      setDisplayedDelegatee(
        isDefaultDelegateeSelf ? "Myself" : shortenIfAddress(defaultDelegatee),
      )
      setFieldValue("delegatee", defaultDelegatee, false)
    }
  }, [defaultDelegatee, isDefaultDelegateeSelf, setFieldValue])

  useEffect(() => {
    updateReceivedValue(values.stakeValue)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.stakeValue])

  useEffect(() => {
    if (prevMode.current !== mode) {
      resetForm({
        values: {
          stakeValue: "0",
          receivedValue: "0",
          delegatee: (defaultDelegatee ?? "") as Address,
        },
        errors: {},
      })
      prevMode.current = mode
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  return (
    <Stack align="center" direction="column" spacing={6}>
      <form style={{ width: "100%" }} onSubmit={handleSubmit}>
        <Stack align="flex-start" color="gray.700" spacing="4px" width="100%">
          <FormControl
            isInvalid={Boolean(touched.stakeValue && errors.stakeValue)}
            width="100%"
          >
            <Stack
              border={
                Boolean(touched.stakeValue && errors.stakeValue)
                  ? "2px solid red"
                  : "gray.200"
              }
              borderRadius="md"
              borderWidth="1px"
              px={3}
              py={4}
              spacing={0}
            >
              <Text>
                {isWithdrawMode
                  ? `${lstToken.symbol} amount`
                  : `${orgToken.symbol} amount`}
              </Text>
              <Tooltip
                display={!tokensAvailable ? "block" : "none"}
                label={`You don't have any tokens to ${
                  isWithdrawMode ? "unstake" : "stake"
                }`}
              >
                <Input
                  _focus={{ borderColor: "currentColor" }}
                  _invalid={{ borderColor: "red.500" }}
                  border="none"
                  borderColor={
                    Boolean(touched.stakeValue && errors.stakeValue)
                      ? "initial"
                      : "currentColor"
                  }
                  focusBorderColor="transparent"
                  fontSize="4xl"
                  fontWeight="bold"
                  isDisabled={!tokensAvailable}
                  name="stakeValue"
                  p={0}
                  placeholder="0"
                  sx={{
                    "::placeholder": {
                      color: "gray.400",
                    },
                  }}
                  textAlign="left"
                  value={values.stakeValue}
                  onBlur={() => setFieldTouched("stakeValue", true, true)}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) =>
                    await handleChangeField(e, "stakeValue")
                  }
                />
              </Tooltip>

              <Flex mr={4} mt="14px" position="absolute" right={0} zIndex={600}>
                <Button
                  bgColor="purple.100"
                  borderRadius="md"
                  color="purple.600"
                  fontWeight="bold"
                  onClick={() => {
                    setFieldValue("stakeValue", maxValue, true)
                    onSetStakeValue(maxValue)
                    updateReceivedValue(maxValue)
                  }}
                >
                  MAX
                </Button>
              </Flex>
            </Stack>

            <FormErrorMessage>
              {touched.stakeValue && errors.stakeValue}
            </FormErrorMessage>
          </FormControl>

          <SwapIcon icon={ArrowDownIcon} width="100%" />

          <FormControl width="100%">
            <Stack
              borderColor="gray.200"
              borderRadius="md"
              borderWidth="1px"
              px={3}
              py={4}
              spacing={0}
            >
              <Text>
                {isWithdrawMode
                  ? `${orgToken.symbol} received`
                  : `${lstToken.symbol} received`}
              </Text>

              <Input
                isReadOnly
                _focus={{ borderColor: "transparent" }}
                _hover={{ borderColor: "transparent" }}
                border="none"
                cursor="default"
                fontSize="4xl"
                fontWeight="bold"
                p={0}
                placeholder="0"
                pointerEvents="none"
                sx={{
                  "::placeholder": {
                    color: "gray.400",
                  },
                  "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  "&::-webkit-input-placeholder": {
                    opacity: 1,
                  },
                }}
                textAlign="left"
                value={values.receivedValue}
              />
            </Stack>
          </FormControl>

          {!isWithdrawMode && tokensAvailable ? (
            <FormControl
              isInvalid={Boolean(touched.delegatee && errors.delegatee)}
              width="100%"
            >
              <Stack
                borderColor="gray.200"
                borderRadius="md"
                borderWidth="1px"
                cursor="pointer"
                mt={1}
                px={3}
                py={4}
                spacing={0}
                onClick={onOpen}
              >
                <Flex alignItems="center" justify="space-between">
                  <Text>Delegate voting power to</Text>
                  <ChevronDownIcon h={6} w={6} />
                </Flex>

                {!delegateeAccount ? (
                  <HStack spacing={2}>
                    {delegateeAccountLoading && <Spinner size="sm" />}

                    <Text fontSize="lg" fontWeight="bold">
                      {displayedDelegatee}
                    </Text>
                  </HStack>
                ) : (
                  <HStack gap={2} justify="flex-start">
                    <Box w="fit-content">
                      <SmallDelegateUserIdentity
                        account={delegateeAccount}
                        avatarSize="sm"
                        displayProfileLink={false}
                        isWhiteLabel={isWhiteLabel}
                        organization={organization}
                      />
                    </Box>
                    {isDRSActive ? (
                      <Stack>
                        {isDRSLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <EligibleBadge
                            useV2IneligibleBadge
                            isEligible={isDRSElegible}
                          />
                        )}
                      </Stack>
                    ) : null}
                  </HStack>
                )}
              </Stack>
              <FormErrorMessage>
                {touched.delegatee && errors.delegatee}
              </FormErrorMessage>
            </FormControl>
          ) : null}

          <Flex pt={2} width="100%">
            {address ? (
              <Button
                fontWeight="medium"
                isDisabled={!tokensAvailable || isDRSLoading}
                type="submit"
                variant="primary"
                width="100%"
              >
                {isWithdrawMode ? "Unstake" : "Stake"}
              </Button>
            ) : (
              <ConnectAccountButton variant="primary" w="full" />
            )}
          </Flex>
        </Stack>
      </form>

      {!isWithdrawMode && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent maxWidth="700px">
            <ModalHeader>
              <Text fontSize="2xl" fontWeight="bold">
                Delegate voting power
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Stack spacing={4}>
                <DelegateeTypeSelector
                  delegateeType={delegationType}
                  meAddress={address as Address}
                  organization={organization}
                  setDelegateeType={(type) => handleDelegationChange(type)}
                />
              </Stack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Stack>
  )
}

export default StakingFormWrapper
